import React, { useEffect, useState, useCallback } from "react";
import Form from "react-bootstrap/Form";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Button from "react-bootstrap/Button";

import Panel from "../Panel";
import PanelSection from "../PanelSection";
import MouseEventUtils from "../../js/MouseEventUtils";
import { ReactComponent as PunctumQuadratum } from "../../svg/shapes/PunctumQuadratum.svg";
import { ReactComponent as PunctumCavum } from "../../svg/shapes/PunctumCavum.svg";
import { ReactComponent as PunctumInclinatum } from "../../svg/shapes/PunctumInclinatum.svg";
import { ReactComponent as Quilisma } from "../../svg/shapes/Quilisma.svg";
import { ReactComponent as OriscusAsc } from "../../svg/shapes/OriscusAsc.svg";
import { ReactComponent as PunctumQuadratumAscLiquescent } from "../../svg/shapes/PunctumQuadratumAscLiquescent.svg";
import { ReactComponent as PunctumQuadratumDesLiquescent } from "../../svg/shapes/PunctumQuadratumDesLiquescent.svg";
import { ReactComponent as TerminatingAscLiquescent } from "../../svg/shapes/TerminatingAscLiquescent.svg";
import { ReactComponent as TerminatingDesLiquescent } from "../../svg/shapes/TerminatingDesLiquescent.svg";
import { ReactComponent as Stropha } from "../../svg/shapes/Stropha.svg";
import { ReactComponent as VirgaShort } from "../../svg/shapes/VirgaShort.svg";
import { ReactComponent as PunctumInclinatumLiquescent } from "../../svg/shapes/PunctumInclinatumLiquescent.svg";
import { ReactComponent as CustosShort } from "../../svg/shapes/CustosShort.svg";
import { ReactComponent as Flat } from "../../svg/shapes/Flat.svg";
import { ReactComponent as Natural } from "../../svg/shapes/Natural.svg";
import { ReactComponent as Sharp } from "../../svg/shapes/Sharp.svg";

import { ReactComponent as HorizontalEpisema } from "../../svg/attachments/HorizontalEpisema.svg";
import { ReactComponent as Ictus } from "../../svg/attachments/Ictus.svg";
import { ReactComponent as PunctumMora } from "../../svg/attachments/PunctumMora.svg";

import { ReactComponent as ChoralSignC } from "../../svg/singleAttachments/ChoralSignC.svg";
import { ReactComponent as ChoralSignT } from "../../svg/singleAttachments/ChoralSignT.svg";
import { ReactComponent as AcuteAccent } from "../../svg/singleAttachments/AcuteAccent.svg";
import { ReactComponent as GraveAccent } from "../../svg/singleAttachments/GraveAccent.svg";
import { ReactComponent as Circle } from "../../svg/singleAttachments/Circle.svg";
import { ReactComponent as Semicircle } from "../../svg/singleAttachments/Semicircle.svg";
import { ReactComponent as ReversedSemicircle } from "../../svg/singleAttachments/ReversedSemicircle.svg";

const shapes = [
    {
      key: "PunctumQuadratum",
      SvgIcon: PunctumQuadratum,
      gabc: ""
    },
    { key: "PunctumCavum", SvgIcon: PunctumCavum, gabc: "r" },
    { key: "PunctumInclinatum", SvgIcon: PunctumInclinatum, gabc: "^" },
    { key: "Quilisma", SvgIcon: Quilisma, gabc: "w" },
    { key: "OriscusAsc", SvgIcon: OriscusAsc, gabc: "o" },
    {
      key: "PunctumQuadratumAscLiquescent",
      SvgIcon: PunctumQuadratumAscLiquescent,
      gabc: "<"
    },
    {
      key: "PunctumQuadratumDesLiquescent",
      SvgIcon: PunctumQuadratumDesLiquescent,
      gabc: ">"
    },
    { key: "Stropha", SvgIcon: Stropha, gabc: "s" },
    { key: "VirgaShort", SvgIcon: VirgaShort, gabc: "v" },
    {
      key: "PunctumQuadratumLiquescent",
      SvgIcon: PunctumQuadratum,
      gabc: "~",
      props: { viewBox: "-100 -61.01 200 123.438" }
    },
    {
      key: "PunctumQuadratumInitioDebilis",
      SvgIcon: TerminatingDesLiquescent,
      initialGabc: "-"
    },
    {
      key: "PunctumInclinatumLiquescent",
      SvgIcon: PunctumInclinatumLiquescent,
      gabc: "^~",
      props: { viewBox: "-75 -53.164 150 105.546" }
    },
    { key: "CustosShort", SvgIcon: CustosShort, gabc: "+" },
    { key: "Flat", SvgIcon: Flat, gabc: "x" },
    { key: "Natural", SvgIcon: Natural, gabc: "y" },
    { key: "Sharp", SvgIcon: Sharp, gabc: "#" }
  ],
  reversedShapes = shapes.slice().reverse(),
  shapesByGabc = shapes.reduce(
    (result, shape) => ({
      ...result,
      [shape.initialGabc || shape.gabc]: shape
    }),
    {}
  );

const attachments = [
    { key: "HorizontalEpisema", SvgIcon: HorizontalEpisema, gabc: "_" },
    { key: "Ictus", SvgIcon: Ictus, gabc: "'" },
    { key: "PunctumMora", SvgIcon: PunctumMora, gabc: "." }
    // { key: "LineaPunctum", SvgIcon: Breve, gabc: "R" } // this should be r0 if the punctum shape is r, but rR seems to work as well
  ],
  attachmentsByGabc = attachments.reduce(
    (result, attachment) => ({ ...result, [attachment.gabc]: attachment }),
    {}
  );
const singleAttachmentRegex = /r[1-5]|\[cs:[^\]]*\]/g;
const singleAttachments = [
  { key: "ChoralSignC", SvgIcon: ChoralSignC, gabc: "[cs:c]" },
  { key: "ChoralSignT", SvgIcon: ChoralSignT, gabc: "[cs:t]" },
  { key: "AcuteAccent", SvgIcon: AcuteAccent, gabc: "r1" },
  { key: "GraveAccent", SvgIcon: GraveAccent, gabc: "r2" },
  { key: "Circle", SvgIcon: Circle, gabc: "r3" },
  { key: "Semicircle", SvgIcon: Semicircle, gabc: "r4" },
  { key: "ReversedSemicircle", SvgIcon: ReversedSemicircle, gabc: "r5" } // see https://gregorio-project.github.io/gabc/summary-gabc.pdf
];
shapes.forEach(elem => {
  let { gabc = "", initialGabc = "" } = elem;
  let caseConverter = "toLowerCase";
  if (gabc[0] === "^") {
    caseConverter = "toUpperCase";
    gabc = gabc.slice(1);
  }

  const escapeForRegex = text =>
    text.replace(/[+.^$\\[\]()]/g, "\\$&").replace(/r/, "r(?!\\d)");
  let prefix = "^" + escapeForRegex(initialGabc) + "([a-m])"[caseConverter]();
  elem.regex = new RegExp(prefix + escapeForRegex(gabc));
  elem.makeNewGabc = (oldGabc, noteGabc) =>
    initialGabc + noteGabc[caseConverter]() + gabc;
});
[...attachments, ...singleAttachments].forEach(elem => {
  if (!("regex" in elem))
    elem.regex = new RegExp(elem.gabc.replace(/[+.^$\\[\]()]/g, "\\$&"));
});

function getGabcModifiers(gabc, list, onlyOne = true) {
  const results = [];
  for (const elem of list) {
    if (elem.regex.test(gabc)) {
      if (onlyOne) return elem;
      results.push(elem);
    }
  }
  if (onlyOne) return null;
  return results;
}
function getGabcShape(gabc) {
  return getGabcModifiers(gabc, reversedShapes);
}
const gabcSpacingRegex = /[ /!]+$/;
function getSpacing(gabc) {
  let spacing = gabcSpacingRegex.exec(gabc);
  if (!spacing) return "";
  return spacing[0][0] === "!" ? "!" : "/";
}

/**
 * Returns GABC for a new shape, based on current GABC
 * @param  {string} gabc        current GABC
 * @param  {function} makeNewGabc function to handle the regex replace
 * @return {string}             new GABC
 */
function getNewGabcShape(gabc, makeNewGabc) {
  let shape = getGabcShape(gabc),
    newGabc = gabc.replace(shape.regex, makeNewGabc);
  return newGabc;
}

function getNewGabcForAttachment(gabc, regex, newGabc) {
  gabc = gabc.replace(regex, "");
  // all spacings and bracketed notations should come at the end of the note:
  let gabcEnd = /^[!/ ]+$/.test(newGabc)
    ? ""
    : gabc.match(/(\[[^\]]*\])*[!/ ]*$/)[0];
  return gabc.slice(0, gabc.length - gabcEnd.length) + newGabc + gabcEnd;
}

function NotePanel(props) {
  const [selectedAttachments, setSelectedAttachments] = useState([]),
    [selectedShape, setSelectedShape] = useState(),
    [selectedSingleAttachment, setSelectedSingleAttachment] = useState(),
    [selectedSpacing, setSelectedSpacing] = useState();
  useEffect(() => {
    let shapeOfAll, attachmentsOfAll, singleAttachmentOfAll, spacingOfAll;
    for (const note of props.selection) {
      if (shapeOfAll || shapeOfAll === undefined) {
        let shape = getGabcShape(note.sourceGabc);
        if (!shapeOfAll) shapeOfAll = shape;
        else shapeOfAll = shapeOfAll === shape ? shape : null;
      }
      if (attachmentsOfAll || attachmentsOfAll === undefined) {
        let noteAttachments = getGabcModifiers(
          note.sourceGabc,
          attachments,
          false
        );
        if (!attachmentsOfAll) attachmentsOfAll = noteAttachments;
        else
          attachmentsOfAll = attachmentsOfAll.filter(attachment =>
            noteAttachments.includes(attachment)
          );
      }
      if (singleAttachmentOfAll || singleAttachmentOfAll === undefined) {
        let singleAttachment = getGabcModifiers(
          note.sourceGabc,
          singleAttachments
        );
        if (!singleAttachmentOfAll) singleAttachmentOfAll = singleAttachment;
        else
          singleAttachmentOfAll =
            singleAttachmentOfAll === singleAttachment
              ? singleAttachment
              : null;
      }
      if (spacingOfAll || spacingOfAll === undefined) {
        let spacing = getSpacing(note.sourceGabc);
        if (!spacingOfAll) spacingOfAll = spacing;
        else spacingOfAll = spacing === spacingOfAll ? spacing : null;
      }
    }
    setSelectedShape(shapeOfAll);
    setSelectedAttachments(
      attachmentsOfAll && attachmentsOfAll.map(attachment => attachment.gabc)
    );
    setSelectedSingleAttachment(singleAttachmentOfAll);
    setSelectedSpacing(spacingOfAll);
  }, [props.selection]);

  const handleKeyDown = useCallback(
    e => {
      let [, upDown, neumaticCut] =
        /^(?:Arrow(Up|Down)|([/!]))$/.exec(e.key) || [];
      if (
        !(upDown && MouseEventUtils.hasModKey(e)) &&
        (e.metaKey || e.ctrlKey || e.altKey)
      )
        return;
      const selection = props.selection,
        onSetShape = props.onSetShape;

      if (upDown) {
        e.preventDefault();
        let offset = upDown === "Up" ? 1 : -1;
        if (MouseEventUtils.hasModKey(e)) offset *= 8;
        const getNoteCharCode = note =>
          note.sourceGabc.match(/^-?([a-m])/i)[1].charCodeAt(0);
        const replaceNoteGabc = note =>
          note.sourceGabc.replace(
            /^(-?)([a-m])/i,
            (all, prefix, note) =>
              prefix + String.fromCharCode(offset + note.charCodeAt(0))
          );
        // first check that nothing in the selection will go out of range (i.e., above "m" or below "a"):
        if (
          !selection.some(
            note =>
              !/[a-m]/i.test(
                String.fromCharCode(offset + getNoteCharCode(note))
              )
          )
        )
          onSetShape(selection, selection.map(replaceNoteGabc));
      } else if (neumaticCut) {
        onSetShape(
          selection,
          selection.map(note =>
            getNewGabcForAttachment(
              note.sourceGabc,
              gabcSpacingRegex,
              selectedSpacing === neumaticCut ? "" : neumaticCut
            )
          )
        );
      } else if (e.key in shapesByGabc) {
        let shape = shapesByGabc[e.key];
        if (selectedShape && selectedShape.key === shape.key) {
          shape = shapesByGabc[""];
        }
        let regex = /^[~^]+$/;
        if (
          selectedShape &&
          regex.test(selectedShape.gabc) &&
          regex.test(e.key)
        ) {
          // special case for liquescent puncta inclinata, since they can't be selected with a single keystroke:
          if (selectedShape.gabc.length === 2) {
            // remove the shape for the key that was just pressed:
            shape = shapesByGabc[selectedShape.gabc.replace(e.key, "")];
          } else if (shape.gabc !== "") {
            // add the shape for the key that was just pressed:
            shape = shapesByGabc["^~"];
          }
        }
        onSetShape(
          selection,
          selection.map(note =>
            getNewGabcShape(note.sourceGabc, shape.makeNewGabc)
          )
        );
      } else if (e.key in attachmentsByGabc) {
        let attachment = attachmentsByGabc[e.key],
          { gabc, regex } = attachment,
          currentlyActive =
            selectedAttachments && selectedAttachments.includes(gabc);
        onSetShape(
          selection,
          selection.map(note =>
            getNewGabcForAttachment(
              note.sourceGabc,
              regex,
              currentlyActive ? "" : gabc
            )
          )
        );
      }
    },
    [
      props.onSetShape,
      props.selection,
      selectedSpacing,
      selectedShape,
      selectedAttachments
    ]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, false);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, false);
    };
  }, [handleKeyDown]);

  return (
    <Panel title="Note" className="border-left">
      <PanelSection title="" className="mb--3">
        <div className="mb-3">
          <Panel.Label>Shapes</Panel.Label>
          {shapes.map(
            ({ key, SvgIcon, props: svgProps = {}, gabc, makeNewGabc }) => (
              <Button
                key={key}
                variant="circle"
                className="btn-svg"
                active={selectedShape && selectedShape.key === key}
                value={gabc}
                onClick={() =>
                  props.onSetShape(
                    props.selection,
                    props.selection.map(note =>
                      getNewGabcShape(note.sourceGabc, makeNewGabc)
                    )
                  )
                }
              >
                <SvgIcon className="svg-in-button" {...svgProps} />
              </Button>
            )
          )}
        </div>
        <div className="mb-3">
          <Panel.Label>Attachments</Panel.Label>
          {attachments.map(({ key, SvgIcon, svgProps = {}, gabc, regex }) => {
            let currentlyActive =
              selectedAttachments && selectedAttachments.includes(gabc);
            return (
              <Button
                key={key}
                variant="square"
                className="btn-svg"
                active={currentlyActive}
                value={gabc}
                onClick={() =>
                  props.onSetShape(
                    props.selection,
                    props.selection.map(note =>
                      getNewGabcForAttachment(
                        note.sourceGabc,
                        regex,
                        currentlyActive ? "" : gabc
                      )
                    )
                  )
                }
              >
                <SvgIcon className="svg-in-button" {...svgProps} />
              </Button>
            );
          })}
          <hr className="my-2" />
          {singleAttachments.map(({ key, SvgIcon, svgProps = {}, gabc }) => {
            let currentlyActive =
              selectedSingleAttachment && selectedSingleAttachment.key === key;
            return (
              <Button
                key={key}
                variant="circle"
                className="btn-svg"
                active={currentlyActive}
                value={gabc}
                onClick={() =>
                  props.onSetShape(
                    props.selection,
                    props.selection.map(note =>
                      getNewGabcForAttachment(
                        note.sourceGabc,
                        singleAttachmentRegex,
                        currentlyActive ? "" : gabc
                      )
                    )
                  )
                }
              >
                <SvgIcon className="svg-in-button" {...svgProps} />
              </Button>
            );
          })}
          {/* TODO: other attachments */}
        </div>
        <div className="mb-3">
          <Panel.Label>Spacing</Panel.Label>
          <ButtonGroup name="spacing" className="w-100" size="sm">
            <Button
              className="d-flex justify-content-center"
              variant="outline-primary"
              value="!"
              active={selectedSpacing === "!"}
              onClick={() =>
                props.onSetShape(
                  props.selection,
                  props.selection.map(note =>
                    getNewGabcForAttachment(
                      note.sourceGabc,
                      gabcSpacingRegex,
                      selectedSpacing === "!" ? "" : "!"
                    )
                  )
                )
              }
            >
              <PunctumQuadratum
                className="svg-in-button"
                style={{ width: "0.7em", height: "auto", marginTop: "0.7em" }}
              />
              <PunctumQuadratum
                className="svg-in-button"
                style={{
                  width: "0.7em",
                  height: "auto",
                  marginBottom: "0.7em"
                }}
              />
            </Button>
            <Button
              className="d-flex justify-content-center"
              variant="outline-primary"
              value="/"
              active={selectedSpacing === "/"}
              onClick={() =>
                props.onSetShape(
                  props.selection,
                  props.selection.map(note =>
                    getNewGabcForAttachment(
                      note.sourceGabc,
                      gabcSpacingRegex,
                      selectedSpacing === "/" ? "" : "/"
                    )
                  )
                )
              }
            >
              <PunctumQuadratum
                className="svg-in-button"
                style={{ width: "0.7em", height: "auto", marginTop: "0.7em" }}
              />
              <div style={{ width: "0.175em" }} />
              <PunctumQuadratum
                className="svg-in-button"
                style={{
                  width: "0.7em",
                  height: "auto",
                  marginBottom: "0.7em"
                }}
              />
            </Button>
          </ButtonGroup>
        </div>
        {props.selection.length === 1 && (
          <label className="mb-3">
            <Panel.Label>Shortcode</Panel.Label>
            <Form.Control value={props.selection[0].sourceGabc} readOnly />
          </label>
        )}
      </PanelSection>
    </Panel>
  );
}

export default NotePanel;
